import React, {useEffect} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import PropTypes from 'prop-types';
import {jwtDecode} from 'jwt-decode';
import {getRouteForUser, ROLE_BASE_ROUTE, ROLES} from '../constants/roles';
import useUser from '../utility/hooks/useUser';
import {getRefreshToken, getToken} from '../utility/jwtUtil';
import {getUser} from '../AppLayout/ApplicationSlice';
import {useDispatch, useSelector} from 'react-redux';
import {Backdrop, CircularProgress, Stack} from '@mui/material';
import Typography from '@mui/material/Typography';

const ProtectedRoute = ({children, allowedRoles}) => {
    const navigate = useNavigate();
    const {pathname} = useLocation();
    const userContext = useUser();
    const dispatch = useDispatch();
    const {user, account} = useUser();
    const isLoadingUserData = useSelector(state => state.application.isLoadingUserData);

    useEffect(() => {
        if (user.id) {
            dispatch(getUser());
        }
    }, [user.id]);

    const getNavigationProps = () => {
        if (!getRefreshToken() || !getToken()) {
            return {route: ROLE_BASE_ROUTE[ROLES.NONE].path, args: {state: {redirectTo: pathname}}};
        }

        if (userContext?.user) {
            if (!userContext.user.hasRoles(allowedRoles)) {
                return {route: getRouteForUser(userContext.user), args: {replace: true}};
            }
        }
    };

    useEffect(() => {
        // Localhost does not use per-school subdomains (always localhost:4000).
        // On real environments, if another tab logged into a different school,
        // redirect the URL to match (same pattern as Header school switcher).
        const isLocalhost = window.location.hostname === 'localhost' ||
            window.location.hostname.endsWith('.localhost');

        if (!isLocalhost) {
            const token = getToken();
            if (token) {
                try {
                    const {Domain: domain} = jwtDecode(token);
                    if (domain && domain !== window.location.host && domain !== window.location.hostname) {
                        window.location.replace(`https://${domain}${pathname}`);
                        return;
                    }
                } catch (e) {
                    console.error(e);
                }
            }
        }

        const navProps = getNavigationProps();

        if (navProps) {
            navigate(navProps.route, navProps.args ?? {});
        }
    }, [userContext, pathname]);

    return !isLoadingUserData && account.isAuthenticated
        ? children
        : <Backdrop
            sx={(theme) => ({color: '#fff', zIndex: theme.zIndex.drawer + 1})}
            open={isLoadingUserData}
        >
            <Stack flexDirection="column" alignItems="center" gap={2}>
                <CircularProgress size={40} color="inherit"/>
                <Typography>Initializing user</Typography>
            </Stack>
        </Backdrop>;
};

ProtectedRoute.propTypes = {
    children: PropTypes.object.isRequired,
    allowedRoles: PropTypes.array.isRequired
};

export default ProtectedRoute;
